import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import type { Me, Overview, Survey } from "../api/types";
import { ConditionPill } from "../components/Badges";
import { BandBars, CountBars, KpiTile } from "../components/Charts";
import { DataTable } from "../components/DataTable";
import { PageHeader, Panel } from "../components/Layout";
import { ConditionLegend, MapPanel } from "../components/MapPanel";
import { CONDITION_COLOR } from "../components/Badges";
import { EmptyState, ErrorState, Skeleton } from "../components/States";
import { km, num } from "../lib/format";

function ScopeNote({ me, roads }: { me: Me; roads: number }) {
  if (me.role !== "viewer" && me.role !== "surveyor") return null;
  if (me.access.all_roads) return null;
  const parts = [...me.access.agencies, ...me.access.counties];
  return (
    <p className="notice">
      Showing {roads} road{roads === 1 ? "" : "s"}: {parts.join(", ") || "roads assigned to you"}
      {me.access.extra_roads ? ` plus ${me.access.extra_roads} assigned road${me.access.extra_roads === 1 ? "" : "s"}` : ""}.
    </p>
  );
}

export function OverviewPage({ me }: { me: Me }) {
  const nav = useNavigate();
  const q = useQuery({ queryKey: ["overview"], queryFn: () => api<Overview>("/api/overview") });

  if (q.isPending) {
    return (
      <>
        <PageHeader title="Overview" />
        <div className="kpis">{Array.from({ length: 6 }, (_, i) => <Skeleton key={i} height={96} />)}</div>
        <Skeleton height={420} />
      </>
    );
  }
  if (q.isError) return <><PageHeader title="Overview" /><ErrorState error={q.error} onRetry={() => q.refetch()} /></>;

  const d = q.data;
  const k = d.kpis;
  if (!k.surveys) {
    return (
      <>
        <PageHeader title="Overview" />
        <ScopeNote me={me} roads={0} />
        <EmptyState
          title={me.role === "viewer" && !me.access.all_roads && !me.access.agencies.length && !me.access.counties.length && !me.access.extra_roads ? "No roads are assigned to you yet" : "No roads surveyed yet"}
          action={me.role === "surveyor" || me.role === "admin" ? <Link className="btn btn-primary" to="/surveys">Start a survey</Link> : undefined}
        >
          {me.role === "viewer" ? "Ask an administrator to give you access to your agency's or county's roads." : "Completed surveys appear here once they have synced."}
        </EmptyState>
      </>
    );
  }

  const share = (v: number) => (k.km ? (v / k.km) * 100 : 0);
  const rated = k.km_by_band.good + k.km_by_band.fair + k.km_by_band.poor;

  return (
    <>
      <PageHeader title="Overview" subtitle={`${num(k.roads)} roads · ${km(k.km)} surveyed`} />
      <ScopeNote me={me} roads={k.roads} />

      <div className="kpis">
        <KpiTile icon="roads" label="Surveyed length" value={km(k.km)} note={`${num(k.surveys)} completed survey${k.surveys === 1 ? "" : "s"}`} to="/roads" />
        <KpiTile icon="check" label="Good" tone="good" value={km(k.km_by_band.good)} bar={{ value: share(k.km_by_band.good), color: CONDITION_COLOR.Good }} note={`${share(k.km_by_band.good).toFixed(0)}% of surveyed length`} />
        <KpiTile icon="defects" label="Fair" tone="fair" value={km(k.km_by_band.fair)} bar={{ value: share(k.km_by_band.fair), color: CONDITION_COLOR.Fair }} note={`${share(k.km_by_band.fair).toFixed(0)}% of surveyed length`} />
        <KpiTile icon="x" label="Poor" tone="poor" value={km(k.km_by_band.poor)} bar={{ value: share(k.km_by_band.poor), color: CONDITION_COLOR.Poor }} note={`${share(k.km_by_band.poor).toFixed(0)}% of surveyed length`} />
        <KpiTile icon="review" label="Verified findings" value={num(k.verified_findings)} note={k.unreviewed_findings ? `+${k.unreviewed_findings} unreviewed AI findings, not counted` : "Confirmed by engineers"} to="/defects" />
        {k.open_review !== null ? (
          <KpiTile icon="image" label="Waiting for review" tone={k.open_review ? "attention" : undefined} value={num(k.open_review)} note="AI findings to confirm or reject" to="/review" />
        ) : null}
      </div>

      {d.recent.some((r) => r.condition_provisional) && rated > 0 ? (
        <p className="notice">Ratings marked with a dashed outline are provisional: some AI findings on those roads are still waiting for review.</p>
      ) : null}
      {rated === 0 ? (
        <p className="notice warn">No condition assessment has been generated for these surveys yet, so all {km(k.km)} show as Not rated. Ratings appear once the decision engine has run.</p>
      ) : null}

      <div className="split">
        <Panel title="Condition map" id="ov-map">
          <MapPanel
            label="Surveyed roads coloured by condition"
            height={440}
            lines={d.map.map((r) => ({ id: r.survey_uuid, points: r.points, color: CONDITION_COLOR[r.condition], label: `${r.road_code} — ${r.road_name ?? ""} · ${r.condition}` }))}
            onSelectLine={(id) => nav(`/roads/${id}`)}
            overlay={<ConditionLegend />}
          />
        </Panel>
        <div className="stack" style={{ gap: 16 }}>
          <Panel title="Condition by agency" id="ov-agency"><BandBars rows={d.by_agency} empty="No agencies recorded" /></Panel>
          <Panel title="Surface type" id="ov-surface"><BandBars rows={d.by_surface} empty="No surfaces recorded" /></Panel>
        </div>
      </div>

      <div className="grid grid-3">
        <Panel title="Counties" id="ov-county"><BandBars rows={d.by_county} empty="No counties recorded" /></Panel>
        <Panel title="Findings by type" id="ov-types"><CountBars rows={d.by_type} empty="No findings yet" /></Panel>
        <Panel title="By assessment" id="ov-domain">
          <dl className="kv" style={{ fontSize: 14, rowGap: 10 }}>
            {(["condition", "assets", "safety"] as const).map((dname) => (
              <div key={dname} style={{ display: "contents" }}>
                <dt><Link to={`/network/${dname}`}>{dname === "condition" ? "Road condition" : dname === "assets" ? "Assets & inventory" : "Road safety"}</Link></dt>
                <dd className="num">{d.by_domain[dname].verified} verified{d.by_domain[dname].unreviewed ? <span className="muted"> · {d.by_domain[dname].unreviewed} unreviewed</span> : null}</dd>
              </div>
            ))}
          </dl>
          {d.assets_verified.length ? (
            <>
              <h3 style={{ fontSize: 14, margin: "16px 0 8px" }}>Verified assets</h3>
              <dl className="kv">{d.assets_verified.map((a) => <div key={a.category} style={{ display: "contents" }}><dt>{a.category}</dt><dd className="num">{a.count}</dd></div>)}</dl>
            </>
          ) : null}
        </Panel>
      </div>

      <Panel title="Recent surveys" id="ov-recent" flush actions={<Link to="/roads" className="btn btn-sm">All roads</Link>}>
        <DataTable<Survey>
          rows={d.recent}
          rowKey={(r) => r.survey_uuid}
          onRowClick={(r) => nav(`/roads/${r.survey_uuid}`)}
          caption="Recent surveys"
          columns={[
            { key: "code", label: "Road", render: (r) => <Link className="row-link" to={`/roads/${r.survey_uuid}`} onClick={(e) => e.stopPropagation()}>{r.road_code}</Link> },
            { key: "name", label: "Name", render: (r) => r.road_name },
            { key: "agency", label: "Agency", render: (r) => r.agency || "—" },
            { key: "county", label: "County", render: (r) => r.county || "—" },
            { key: "len", label: "Length", align: "right", render: (r) => km(r.length_km) },
            { key: "cond", label: "Condition", render: (r) => <ConditionPill value={r.condition} provisional={r.condition_provisional} /> },
            { key: "date", label: "Survey date", render: (r) => r.survey_date },
          ]}
        />
      </Panel>
    </>
  );
}
